import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * Una fila de lista que entra y sale con movimiento.
 *
 * Se usa dentro de un `<AnimatePresence>`: al filtrar una lista, lo que
 * coincide aparece y lo que deja de coincidir se va, en vez de que la lista
 * salte de un estado al otro. Sin eso, escribir en el buscador se siente como
 * si la pantalla parpadeara y cuesta seguir qué quedó.
 *
 * ENTRA distinto de como SALE, y es a propósito. Lo que llega baja un poco y se
 * asienta; lo que se va se achica y se desvanece más rápido. Si el gesto fuera
 * el mismo en los dos sentidos no se distinguiría una cosa de la otra —que es
 * justamente lo que hay que comunicar—, y la salida corta porque nadie quiere
 * esperar a que se despida algo que ya descartó.
 *
 * Va con framer y no con CSS por una razón concreta del panel: Dashboard.css
 * apaga `transform` y `animation` con `!important` sobre todo lo que hay
 * adentro de `.dashboard-container`. Una animación declarada en CSS ahí no
 * corre, y encima falla en silencio —anda en el onboarding y muere en el
 * panel—. Framer escribe el estilo cuadro a cuadro, pero para los transforms
 * necesita además la excepción de Dashboard.css (`[data-fila-animada]`), igual
 * que la que ya tenía el interruptor.
 *
 * Con `prefers-reduced-motion` no se mueve nada: aparece y desaparece y listo.
 */
export default function FilaAnimada({ children, className }) {
  const menosMovimiento = useReducedMotion();

  if (menosMovimiento) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      data-fila-animada
      className={className}
      // `layout` reacomoda a las vecinas cuando una se va, en vez de que el
      // resto salte al hueco.
      layout
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{
        // Entrar: suave y con cuerpo. Salir: la mitad de tiempo.
        opacity: { duration: 0.22 },
        y: { duration: 0.26, ease: [0.22, 1, 0.36, 1] },
        scale: { duration: 0.14 },
        layout: { duration: 0.24, ease: [0.22, 1, 0.36, 1] },
      }}
    >
      {children}
    </motion.div>
  );
}
